<template>
  <el-dialog
    :title="`商户价配置 - ${priceBook ? priceBook.name : ''}`"
    :visible.sync="dialogVisible"
    width="80%"
    top="5vh"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <div v-if="priceBook" class="book-info">
      <el-tag size="small">{{ projectTypeText }}</el-tag>
      <el-tag v-if="priceBook.isFallback === 'YES'" size="small" type="warning">兜底盘</el-tag>
      <span v-if="isCeiling" class="book-info-tip">吊顶价盘：一个商户需按扣板型号分别配价</span>
      <span v-else class="book-info-tip">一个商户一个价</span>
    </div>

    <!-- 搜索栏 -->
    <div class="filter-bar">
      <el-input
        v-model="searchName"
        placeholder="请输入商户名称"
        size="small"
        clearable
        style="width: 220px"
        @keyup.enter.native="handleSearch"
      />
      <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">查询</el-button>
      <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      <div class="filter-bar-right">
        <el-button
          size="small"
          icon="el-icon-download"
          :loading="templateLoading"
          @click="handleDownloadTemplate"
        >下载模板</el-button>
        <el-button type="warning" size="small" icon="el-icon-upload2" plain @click="handleOpenImport">批量导入</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增商户价</el-button>
        <el-button
          type="danger"
          size="small"
          icon="el-icon-delete"
          plain
          :disabled="!selectedRows.length"
          @click="handleBatchDelete"
        >批量删除</el-button>
      </div>
    </div>

    <!-- 商户价表格 -->
    <el-table
      v-loading="tableLoading"
      :data="tableData"
      border
      stripe
      size="small"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="45" align="center" />
      <el-table-column type="index" label="序号" width="60" align="center" />
      <el-table-column prop="merchantName" label="商户名称" min-width="160" show-overflow-tooltip />
      <el-table-column prop="merchantPhone" label="商户电话" min-width="120" />
      <el-table-column v-if="isCeiling" label="扣板型号" min-width="140">
        <template slot-scope="scope">
          <span v-if="scope.row.goodsFilterName">{{ scope.row.goodsFilterName }}</span>
          <span v-else class="empty-text">-</span>
        </template>
      </el-table-column>
      <el-table-column label="商户单价" width="130" align="center">
        <template slot-scope="scope">
          <span class="price-text">{{ scope.row.price }}</span>
          <span class="price-unit"> 元/㎡</span>
        </template>
      </el-table-column>
      <el-table-column label="调价信息" min-width="220" align="left">
        <template slot-scope="scope">
          <template v-if="scope.row.activeAdjustTask">
            <div class="adjust-info">
              <el-tag size="mini" type="danger">调价中</el-tag>
              <span class="adjust-price">{{ scope.row.activeAdjustTask.adjustedPrice }}</span>
              <span class="adjust-unit"> 元/㎡</span>
              <span class="adjust-tip">
                （原价 {{ scope.row.price }} → 至
                {{ formatAdjustEnd(scope.row.activeAdjustTask.effectiveEndTime) }}）
              </span>
            </div>
          </template>
          <span v-else class="empty-text">-</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90" align="center">
        <template slot-scope="scope">
          <el-switch
            :value="scope.row.status === 'ENABLE'"
            @change="handleToggleStatus(scope.row, $event)"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="150" align="center" fixed="right">
        <template slot-scope="scope">
          <!-- <el-button type="text" size="small" icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button> -->
          <el-button
            type="text"
            size="small"
            icon="el-icon-delete"
            style="color: #f56c6c"
            @click="handleDelete(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager-bar">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="page.total"
        :current-page="page.currentPage"
        :page-size="page.pageSize"
        :page-sizes="[10, 20, 50]"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <!-- 新增商户价弹窗 -->
    <el-dialog
      title="新增商户价"
      :visible.sync="addDialogVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleAddClosed"
    >
      <el-form ref="addForm" :model="addForm" :rules="addRules" label-width="110px">
        <el-form-item label="商户" prop="merchantId">
          <el-select
            v-model="addForm.merchantId"
            filterable
            remote
            :remote-method="searchMerchant"
            :loading="merchantLoading"
            placeholder="输入商户名称搜索"
            style="width: 100%"
          >
            <el-option
              v-for="item in merchantOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="isCeiling" label="扣板型号" prop="goodsFilterId">
          <el-select
            v-model="addForm.goodsFilterId"
            filterable
            placeholder="请选择扣板型号"
            style="width: 100%"
          >
            <el-option-group
              v-for="group in goodsFilterGroups"
              :key="group.label"
              :label="group.label"
            >
              <el-option
                v-for="item in group.options"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-option-group>
          </el-select>
        </el-form-item>
        <el-form-item label="商户单价" prop="price">
          <el-input-number
            v-model="addForm.price"
            :min="0"
            :precision="2"
            controls-position="right"
            style="width: 100%"
            placeholder="商户单价（元/㎡）"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="addSubmitting" @click="handleAddSubmit">确定</el-button>
      </span>
    </el-dialog>

    <!-- 编辑商户价弹窗（仅可改价格与状态） -->
    <el-dialog
      title="编辑商户价"
      :visible.sync="editDialogVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form label-width="110px">
        <el-form-item label="商户">
          <span>{{ editForm.merchantName }}（{{ editForm.merchantPhone || '-' }}）</span>
        </el-form-item>
        <el-form-item v-if="isCeiling" label="扣板型号">
          <span>{{ editForm.goodsFilterName || '-' }}</span>
        </el-form-item>
        <el-form-item label="商户单价" required>
          <el-input-number
            v-model="editForm.price"
            :min="0"
            :precision="2"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch
            v-model="editForm.status"
            active-value="ENABLE"
            inactive-value="DISABLE"
            active-text="启用"
            inactive-text="停用"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="editSubmitting" @click="handleEditSubmit">确定</el-button>
      </span>
    </el-dialog>

    <!-- 批量导入弹窗 -->
    <el-dialog
      title="批量导入商户价"
      :visible.sync="importDialogVisible"
      width="80%"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleImportClosed"
    >
      <div class="import-steps">
        <div class="import-step">
          <span class="step-num">1</span>
          <el-button type="text" icon="el-icon-download" :loading="templateLoading" @click="handleDownloadTemplate">
            下载导入模板
          </el-button>
          <span class="step-tip">模板含填写示例与商户/型号清单，只填名称即可，「示例：」开头的行会自动跳过</span>
        </div>
        <div class="import-step">
          <span class="step-num">2</span>
          <el-radio-group v-model="importMode" size="small">
            <el-radio-button label="INSERT">新增模式</el-radio-button>
            <el-radio-button label="OVERWRITE">覆盖模式</el-radio-button>
          </el-radio-group>
          <span class="step-tip">{{ importMode === 'INSERT' ? '已配置过的商户价会报错' : '已配置过的商户价将更新价格' }}</span>
        </div>
        <div class="import-step">
          <span class="step-num">3</span>
          <el-upload
            ref="importUpload"
            action="#"
            :auto-upload="false"
            :limit="1"
            accept=".xlsx,.xls"
            :file-list="importFileList"
            :on-change="handleImportFileChange"
            :on-remove="handleImportFileRemove"
            :on-exceed="handleImportFileExceed"
          >
            <el-button size="small" icon="el-icon-folder-opened">选择文件</el-button>
          </el-upload>
          <el-button
            type="primary"
            size="small"
            :disabled="!importFile"
            :loading="previewLoading"
            @click="handlePreview"
          >上传预览</el-button>
        </div>
      </div>

      <template v-if="previewResult">
        <el-alert
          :title="`共 ${previewResult.totalRows} 行，有效 ${previewResult.validRows} 行，无效 ${previewResult.invalidRows} 行${previewResult.invalidRows > 0 ? '（红色行不会导入，请修正后重新上传）' : ''}`"
          :type="previewResult.invalidRows > 0 ? 'warning' : 'success'"
          :closable="false"
          show-icon
          style="margin-bottom: 12px"
        />
        <el-table
          :data="previewResult.rows"
          border
          stripe
          size="small"
          max-height="360"
          :row-class-name="previewRowClass"
        >
          <el-table-column prop="rowNo" label="行号" width="70" align="center" />
          <el-table-column prop="merchantName" label="商户名称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="merchantPhone" label="商户电话" min-width="110">
            <template slot-scope="scope">{{ scope.row.merchantPhone || '-' }}</template>
          </el-table-column>
          <el-table-column v-if="isCeiling" prop="goodsFilterName" label="型号名" min-width="120">
            <template slot-scope="scope">{{ scope.row.goodsFilterName || '-' }}</template>
          </el-table-column>
          <el-table-column prop="price" label="单价" width="100" align="center" />
          <el-table-column prop="action" label="操作" width="80" align="center" />
          <el-table-column label="校验结果" min-width="160">
            <template slot-scope="scope">
              <span v-if="scope.row.error" class="error-text">{{ scope.row.error }}</span>
              <span v-else style="color: #67c23a">通过</span>
            </template>
          </el-table-column>
        </el-table>
      </template>

      <span slot="footer" class="dialog-footer">
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :disabled="!validPreviewRows.length"
          :loading="confirmLoading"
          @click="handleConfirmImport"
        >确认导入{{ validPreviewRows.length ? `（${validPreviewRows.length} 行）` : '' }}</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import {
  findPriceBookMerchant,
  addPriceBookMerchant,
  updatePriceBookMerchant,
  deletePriceBookMerchant,
  downloadMerchantImportTemplate,
  previewMerchantImport,
  confirmMerchantImport,
} from "@/api/work/waterProof/merchantPrice";
import { findShop } from "@/api/salesCloud/shop";
import { findPureWaterGoodsByRootDepartId } from "@/api/salesCloud/goodsQr";

export default {
  name: "MerchantPriceDrawer",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    priceBook: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dialogVisible: false,
      tableLoading: false,
      tableData: [],
      searchName: "",
      selectedRows: [],
      page: { total: 0, currentPage: 1, pageSize: 10 },
      // 新增
      addDialogVisible: false,
      addSubmitting: false,
      addForm: { merchantId: "", goodsFilterId: "", price: null },
      addRules: {
        merchantId: [{ required: true, message: "请选择商户", trigger: "change" }],
        goodsFilterId: [{ required: true, message: "请选择扣板型号", trigger: "change" }],
        price: [{ required: true, message: "请输入商户单价", trigger: "blur" }],
      },
      merchantOptions: [],
      merchantLoading: false,
      goodsFilterGroups: [],
      // 编辑
      editDialogVisible: false,
      editSubmitting: false,
      editForm: { id: "", merchantName: "", merchantPhone: "", goodsFilterName: "", price: null, status: "ENABLE" },
      // 批量导入
      templateLoading: false,
      importDialogVisible: false,
      importMode: "INSERT",
      importFile: null,
      importFileList: [],
      previewLoading: false,
      previewResult: null,
      confirmLoading: false,
    };
  },
  computed: {
    // 预览中校验通过的行（确认导入时原样回传）
    validPreviewRows() {
      if (!this.previewResult || !this.previewResult.rows) return [];
      return this.previewResult.rows.filter((r) => !r.error);
    },
    isCeiling() {
      return this.priceBook && this.priceBook.projectType === "HANG_CEILING";
    },
    projectTypeText() {
      const map = { WATERPROOF: "防水", BACKFILL: "回填", HANG_CEILING: "吊顶" };
      return this.priceBook ? map[this.priceBook.projectType] || this.priceBook.projectType : "";
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val;
        if (val && this.priceBook) {
          this.searchName = "";
          this.selectedRows = [];
          this.page.currentPage = 1;
          this.handleList();
          if (this.isCeiling) this.loadGoodsFilters();
        }
      },
    },
    dialogVisible(val) {
      this.$emit("update:visible", val);
    },
  },
  methods: {
    parseData(res) {
      return typeof res.data === "string" ? JSON.parse(res.data) : res.data || {};
    },
    // 调价失效时间格式化（毫秒时间戳 → yyyy-MM-dd）
    formatAdjustEnd(v) {
      if (v == null || v === "") return "-";
      if (typeof v === "string" && !/^\d+$/.test(v)) return v;
      const d = new Date(Number(v));
      if (isNaN(d.getTime())) return "-";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    // 商户价列表（接口 page 从 0 开始）
    handleList() {
      if (!this.priceBook) return;
      this.tableLoading = true;
      findPriceBookMerchant({
        priceBookId: this.priceBook.id,
        merchantName: this.searchName || undefined,
        page: this.page.currentPage - 1,
        size: this.page.pageSize,
      })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.tableData = data.rows || [];
            this.page.total = data.total || 0;
          } else {
            this.$message.error(res.message || "加载失败");
          }
        })
        .catch((err) => {
          console.log("MerchantPriceDrawer handleList error:", err);
          this.$message.error("加载异常");
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    handleSearch() {
      this.page.currentPage = 1;
      this.handleList();
    },
    handleReset() {
      this.searchName = "";
      this.page.currentPage = 1;
      this.handleList();
    },
    handlePageChange(page) {
      this.page.currentPage = page;
      this.handleList();
    },
    handleSizeChange(size) {
      this.page.pageSize = size;
      this.page.currentPage = 1;
      this.handleList();
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection || [];
    },
    // 吊顶：加载扣板型号（calculateType=2 的过滤器）
    loadGoodsFilters() {
      findPureWaterGoodsByRootDepartId("HANG_CEILING")
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res) || [];
            this.goodsFilterGroups = (Array.isArray(data) ? data : [])
              .filter((item) => String(item.calculateType) === "2")
              .map((item) => ({
                label: item.name,
                options: (item.filters || []).map((f) => ({ value: f.id, label: f.value })),
              }))
              .filter((g) => g.options.length);
          }
        })
        .catch((err) => {
          console.log("loadGoodsFilters error:", err);
        });
    },
    // 商户远程搜索（GET /shop）
    searchMerchant(keyword) {
      this.merchantLoading = true;
      const params = { page: 1, size: 20 };
      if (keyword) params.shopName = keyword;
      findShop(params)
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            const rows = Array.isArray(data) ? data : data.rows || [];
            this.merchantOptions = rows.map((r) => ({
              value: r.id,
              label: r.mobile ? `${r.shopName}（${r.mobile}）` : r.shopName,
            }));
          }
        })
        .catch((err) => {
          console.log("searchMerchant error:", err);
        })
        .finally(() => {
          this.merchantLoading = false;
        });
    },
    handleAdd() {
      this.addForm = { merchantId: "", goodsFilterId: "", price: null };
      this.searchMerchant("");
      this.addDialogVisible = true;
    },
    handleAddSubmit() {
      this.$refs.addForm.validate((valid) => {
        if (!valid) return;
        this.addSubmitting = true;
        const payload = {
          priceBookId: this.priceBook.id,
          merchantId: this.addForm.merchantId,
          price: this.addForm.price,
        };
        if (this.isCeiling) payload.goodsFilterId = this.addForm.goodsFilterId;
        addPriceBookMerchant(payload)
          .then((res) => {
            if (res.code === "200") {
              this.$message.success("新增成功");
              this.addDialogVisible = false;
              this.handleList();
            } else {
              this.$message.error(res.message || "新增失败");
            }
          })
          .catch((err) => {
            console.log("handleAddSubmit error:", err);
            this.$message.error("新增异常");
          })
          .finally(() => {
            this.addSubmitting = false;
          });
      });
    },
    handleAddClosed() {
      this.$refs.addForm && this.$refs.addForm.clearValidate();
    },
    handleEdit(row) {
      this.editForm = {
        id: row.id,
        merchantName: row.merchantName,
        merchantPhone: row.merchantPhone,
        goodsFilterName: row.goodsFilterName,
        price: row.price,
        status: row.status || "ENABLE",
      };
      this.editDialogVisible = true;
    },
    handleEditSubmit() {
      if (this.editForm.price === null || this.editForm.price === undefined || this.editForm.price === "") {
        this.$message.warning("请输入商户单价");
        return;
      }
      this.editSubmitting = true;
      updatePriceBookMerchant({
        id: this.editForm.id,
        price: this.editForm.price,
        status: this.editForm.status,
      })
        .then((res) => {
          if (res.code === "200") {
            this.$message.success("编辑成功");
            this.editDialogVisible = false;
            this.handleList();
          } else {
            this.$message.error(res.message || "编辑失败");
          }
        })
        .catch((err) => {
          console.log("handleEditSubmit error:", err);
          this.$message.error("编辑异常");
        })
        .finally(() => {
          this.editSubmitting = false;
        });
    },
    handleToggleStatus(row, val) {
      const target = val ? "ENABLE" : "DISABLE";
      updatePriceBookMerchant({ id: row.id, price: row.price, status: target })
        .then((res) => {
          if (res.code === "200") {
            this.$set(row, "status", target);
            this.$message.success(target === "ENABLE" ? "已启用" : "已停用");
          } else {
            this.$message.error(res.message || "状态切换失败");
          }
        })
        .catch((err) => {
          console.log("handleToggleStatus error:", err);
          this.$message.error("状态切换异常");
        });
    },
    handleDelete(row) {
      this.$confirm(`确定删除商户「${row.merchantName}」的价格配置吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => this.doDelete(row.id))
        .catch(() => {});
    },
    handleBatchDelete() {
      if (!this.selectedRows.length) {
        this.$message.warning("请先选择要删除的项");
        return;
      }
      this.$confirm(`确定删除选中的 ${this.selectedRows.length} 条商户价吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => this.doDelete(this.selectedRows.map((r) => r.id).join(",")))
        .catch(() => {});
    },
    doDelete(ids) {
      deletePriceBookMerchant(ids)
        .then((res) => {
          if (res.code === "200") {
            this.$message.success("删除成功");
            this.selectedRows = [];
            this.handleList();
          } else {
            this.$message.error(res.message || "删除失败");
          }
        })
        .catch((err) => {
          console.log("doDelete error:", err);
          this.$message.error("删除异常");
        });
    },
    handleClosed() {
      this.tableData = [];
      this.selectedRows = [];
      this.searchName = "";
      this.page = { total: 0, currentPage: 1, pageSize: 10 };
      this.$emit("close");
    },
    /* ============ Excel 批量导入（三步） ============ */
    // 第 1 步：下载模板
    handleDownloadTemplate() {
      if (!this.priceBook) return;
      this.templateLoading = true;
      downloadMerchantImportTemplate(this.priceBook.id)
        .then((response) => {
          let filename = `商户价导入模板-${this.priceBook.name}.xlsx`;
          const disposition = response.headers && response.headers["content-disposition"];
          if (disposition) {
            const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
            if (match && match[1]) filename = decodeURI(match[1].replace(/"/g, ""));
          }
          const blob = new Blob([response.data], { type: "application/octet-stream" });
          if (typeof window.navigator.msSaveBlob !== "undefined") {
            window.navigator.msSaveBlob(blob, filename);
          } else {
            const blobURL = window.URL.createObjectURL(blob);
            const tempLink = document.createElement("a");
            tempLink.style.display = "none";
            tempLink.href = blobURL;
            tempLink.setAttribute("download", filename);
            if (typeof tempLink.download === "undefined") {
              tempLink.setAttribute("target", "_blank");
            }
            document.body.appendChild(tempLink);
            tempLink.click();
            document.body.removeChild(tempLink);
            window.URL.revokeObjectURL(blobURL);
          }
        })
        .catch((err) => {
          console.log("handleDownloadTemplate error:", err);
          this.$message.error("模板下载失败");
        })
        .finally(() => {
          this.templateLoading = false;
        });
    },
    handleOpenImport() {
      this.importMode = "INSERT";
      this.importFile = null;
      this.importFileList = [];
      this.previewResult = null;
      this.importDialogVisible = true;
    },
    handleImportFileChange(file, fileList) {
      this.importFile = file.raw;
      this.importFileList = fileList.slice(-1);
      this.previewResult = null;
    },
    handleImportFileRemove() {
      this.importFile = null;
      this.importFileList = [];
    },
    handleImportFileExceed(files) {
      // 超出 limit 时替换为新文件
      this.$refs.importUpload.clearFiles();
      this.$refs.importUpload.handleStart(files[0]);
    },
    // 第 2 步：上传预览
    handlePreview() {
      if (!this.importFile) {
        this.$message.warning("请先选择文件");
        return;
      }
      this.previewLoading = true;
      const formData = new FormData();
      formData.append("file", this.importFile);
      formData.append("priceBookId", this.priceBook.id);
      formData.append("mode", this.importMode);
      previewMerchantImport(formData)
        .then((res) => {
          if (res.code === "200") {
            this.previewResult = this.parseData(res);
          } else {
            this.previewResult = null;
            this.$message.error(res.message || "预览失败");
          }
        })
        .catch((err) => {
          console.log("handlePreview error:", err);
          this.$message.error("预览异常");
        })
        .finally(() => {
          this.previewLoading = false;
        });
    },
    previewRowClass({ row }) {
      return row.error ? "preview-error-row" : "";
    },
    // 第 3 步：确认导入（只回传校验通过的行）
    handleConfirmImport() {
      if (!this.validPreviewRows.length) return;
      this.confirmLoading = true;
      confirmMerchantImport({
        priceBookId: this.priceBook.id,
        mode: this.importMode,
        rows: this.validPreviewRows.map((r) => ({
          merchantName: r.merchantName,
          merchantPhone: r.merchantPhone || "",
          goodsFilterName: r.goodsFilterName || "",
          price: r.price,
        })),
      })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.$message.success(`导入成功，共 ${data.imported != null ? data.imported : this.validPreviewRows.length} 条`);
            this.importDialogVisible = false;
            this.handleList();
          } else {
            this.$message.error(res.message || "导入失败");
          }
        })
        .catch((err) => {
          console.log("handleConfirmImport error:", err);
          this.$message.error("导入异常");
        })
        .finally(() => {
          this.confirmLoading = false;
        });
    },
    handleImportClosed() {
      this.importFile = null;
      this.importFileList = [];
      this.previewResult = null;
      this.$refs.importUpload && this.$refs.importUpload.clearFiles();
    },
  },
};
</script>

<style scoped>
.book-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.book-info-tip {
  font-size: 12px;
  color: #909399;
}
.filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.filter-bar-right {
  margin-left: auto;
  display: flex;
  gap: 8px;
}
.pager-bar {
  margin-top: 12px;
  text-align: right;
}
.price-text {
  color: #e6a23c;
  font-weight: 600;
}
.price-unit {
  color: #909399;
  font-size: 12px;
}
.empty-text {
  color: #c0c4cc;
}
.adjust-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  .adjust-price {
    color: #f56c6c;
    font-weight: 600;
  }
  .adjust-unit {
    color: #909399;
    font-size: 12px;
  }
  .adjust-tip {
    color: #909399;
    font-size: 12px;
  }
}
.import-steps {
  margin-bottom: 12px;
}
.import-step {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.step-num {
  flex: none;
  width: 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 12px;
}
.step-tip {
  font-size: 12px;
  color: #909399;
}
.error-text {
  color: #f56c6c;
}
::v-deep .preview-error-row {
  background: #fef0f0;
}
</style>
